import {useState, useEffect, useMemo} from 'react';
import {format} from 'date-fns';
import {Link} from 'react-router-dom';
import {
  RefreshCw,
  Play,
  Square,
  ExternalLink,
  Database,
  FileText,
} from 'lucide-react';
import {
  useGetMigrationRunDetailQuery,
  useRetryFailedMigrationMutation,
  useStartMigrationRunMutation,
} from '~/store/rtkQuery';
import {Migrations} from '~/modules/api';
import {useToaster} from '~/context/ToasterContext';

export const MigrationRunDetail = ({runId}: {runId: string}) => {
  const {showToast} = useToaster();
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);

  // Helper functions for external links
  const getLogsUrl = () => {
    return `https://console.cloud.google.com/logs/query;query=jsonPayload.migrationRunId%3D%22${runId}%22?project=luna-ai-415021`;
  };

  const getDatabaseUrl = () => {
    return `https://console.firebase.google.com/project/luna-ai-415021/firestore/data/migrationRuns/${runId}`;
  };

  const {data, isLoading, error, refetch} = useGetMigrationRunDetailQuery({
    runId,
  });
  const [retryMigration, {isLoading: isRetrying}] =
    useRetryFailedMigrationMutation();
  const [startMigrationRun, {isLoading: isStarting}] =
    useStartMigrationRunMutation();

  const status = data?.status;

  const handleRetryMigration = async () => {
    try {
      await retryMigration({runId});
      setShowRetryConfirm(false);
      refetch();
    } catch (error) {
      showToast({
        type: 'error',
        message: `Failed to retry migration - ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleStartMigration = async () => {
    try {
      await startMigrationRun({runId, action: 'start'});
      refetch();
    } catch (error) {
      showToast({
        type: 'error',
        message: `Failed to start migration - ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleStartMigrationScan = async () => {
    try {
      await startMigrationRun({runId, action: 'scan'});
      refetch();
    } catch (error) {
      showToast({
        type: 'error',
        message: `Failed to start migration scan - ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleStopMigration = async () => {
    try {
      await startMigrationRun({runId, action: 'stop'});
      refetch();
    } catch (error) {
      showToast({
        type: 'error',
        message: `Failed to stop migration - ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const errors = useMemo(() => {
    return data?.batches?.flatMap(b => b.errors ?? []).filter(Boolean) ?? [];
  }, [data]);

  const getStatusBadge = (
    status: Migrations.MigrationRunStatus | Migrations.MigrationBatchStatus,
  ) => {
    const statusColors = {
      [Migrations.MigrationRunStatus.created]:
        'badge badge-lg badge-info text-info-content',
      [Migrations.MigrationRunStatus.scanning]:
        'badge badge-lg badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.queued]:
        'badge badge-lg badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.processing]:
        'badge badge-lg badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.completed]:
        'badge badge-lg badge-success text-success-content',
      [Migrations.MigrationRunStatus.failed]:
        'badge badge-lg badge-error text-error-content',
    };

    return <span className={statusColors[status]}>{status}</span>;
  };

  // Poll if status is scanning or processing
  useEffect(() => {
    if (!status) return;

    const isProcessing =
      status === Migrations.MigrationRunStatus.created ||
      status === Migrations.MigrationRunStatus.scanning ||
      status === Migrations.MigrationRunStatus.processing;
    if (!isProcessing) return;

    const interval = setInterval(
      () => {
        refetch();
      },
      status === Migrations.MigrationRunStatus.processing ? 5000 : 1000,
    );

    return () => clearInterval(interval);
  }, [status, refetch]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div role="alert" className="alert alert-error">
          <span>Error: {error.message}</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div role="alert" className="alert alert-warning">
          <span>No migration run data found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation */}
      <div className="text-sm breadcrumbs mb-4">
        <ul>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li>
            <Link to="/system/migrations">Migrations</Link>
          </li>
          <li>{data.id}</li>
        </ul>
      </div>

      <div className="mb-6 gap-2 flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{data.name}</h1>
            <p className="text-gray-600">Migration Run ID: {data.id}</p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(data.status)}
            <button className="btn btn-outline" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          {[
            Migrations.MigrationRunStatus.created,
            Migrations.MigrationRunStatus.queued,
            Migrations.MigrationRunStatus.completed,
            Migrations.MigrationRunStatus.failed,
          ].includes(data.status) && (
            <button
              className="btn btn-success"
              onClick={handleStartMigrationScan}
              disabled={isStarting}>
              {isStarting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Scanning...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Scan for new data
                </>
              )}
            </button>
          )}
          {[Migrations.MigrationRunStatus.queued].includes(data.status) && (
            <button
              className="btn btn-success"
              onClick={handleStartMigration}
              disabled={isStarting}>
              {isStarting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Starting...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Migration
                </>
              )}
            </button>
          )}
          {data.status === Migrations.MigrationRunStatus.failed && (
            <button
              className="btn btn-error"
              onClick={() => setShowRetryConfirm(true)}
              disabled={isRetrying}>
              {isRetrying ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Retrying...
                </>
              ) : (
                'Retry Migration'
              )}
            </button>
          )}
          {data.status === Migrations.MigrationRunStatus.processing && (
            <button
              className="btn btn-error"
              onClick={handleStopMigration}
              disabled={isStarting}>
              {isStarting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Stopping...
                </>
              ) : (
                <>
                  <Square className="w-4 h-4" />
                  Stop Migration
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Timing</h2>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Created:</span>
                <div className="text-sm text-gray-600">
                  {format(new Date(data.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                </div>
              </div>
              {data.completedAt && (
                <div>
                  <span className="font-medium">Completed:</span>
                  <div className="text-sm text-gray-600">
                    {format(
                      new Date(data.completedAt),
                      'MMM dd, yyyy HH:mm:ss',
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Configuration</h2>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Max Concurrent Batches:</span>
                <div className="text-sm text-gray-600">
                  {data.maxConcurrentBatches}
                </div>
              </div>
              {data.initialCursor && (
                <div>
                  <span className="font-medium">Initial Cursor:</span>
                  <div className="text-sm text-gray-600 font-mono">
                    {data.initialCursor}
                  </div>
                </div>
              )}
              {data.lastScannedCursor && (
                <div>
                  <span className="font-medium">Last Scanned Cursor:</span>
                  <div className="text-sm text-gray-600 font-mono">
                    {data.lastScannedCursor}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title">Links</h2>
            <div className="space-y-2">
              <div className="text-sm text-blue-600 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                <a
                  href={data.logsLink}
                  target="_blank"
                  rel="noopener noreferrer">
                  View Google Cloud Logs
                </a>
              </div>

              <div className="text-sm text-blue-600 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                <a
                  href={data.firestoreLink}
                  target="_blank"
                  rel="noopener noreferrer">
                  View in Firestore
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Summary */}
      {(data.rowStatusCounts ||
        (data.batchStatusCounts &&
          Object.keys(data.batchStatusCounts).length > 0)) && (
        <div className="card bg-base-100 shadow mb-8">
          <div className="card-body">
            <h2 className="card-title">Processing Summary</h2>

            {/* Row Processing Section */}
            {data.rowStatusCounts && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">
                  Row Processing
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {data.rowStatusCounts.created}
                    </div>
                    <div className="text-sm text-gray-600">Created</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {data.rowStatusCounts.success}
                    </div>
                    <div className="text-sm text-gray-600">Success</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {data.rowStatusCounts.error}
                    </div>
                    <div className="text-sm text-gray-600">Error</div>
                  </div>
                </div>
              </div>
            )}

            {/* Batch Processing Section */}
            {data.batchStatusCounts &&
              Object.keys(data.batchStatusCounts).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700">
                    Batch Processing
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(data.batchStatusCounts).map(
                      ([status, count]) => (
                        <div
                          key={status}
                          className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-gray-800">
                            {count}
                          </div>
                          <div className="text-xs text-gray-600 capitalize">
                            {status}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Parameters */}
      {data.params && Object.keys(data.params).length > 0 && (
        <div className="card bg-base-100 shadow mb-8">
          <div className="card-body">
            <h2 className="card-title">Parameters</h2>
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.params).map(([key, value]) => (
                    <tr key={key}>
                      <td className="font-medium">{key}</td>
                      <td className="font-mono text-sm">
                        {typeof value === 'object'
                          ? JSON.stringify(value, null, 2)
                          : String(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="card bg-base-100 shadow mb-8">
          <div className="card-body">
            <h2 className="card-title">Errors</h2>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>Record ID</th>
                    <th>Error</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map(error => (
                    <tr key={error.message}>
                      <td>{'recordId' in error ? error.recordId : '-'}</td>
                      <td>{error.message}</td>
                      <td>
                        {error.data ? (
                          <pre className="text-xs text-gray-600">
                            {JSON.stringify(error.data, null, 2)}
                          </pre>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Retry Confirmation Modal */}
      {showRetryConfirm && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <form method="dialog">
              <button
                className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                onClick={() => setShowRetryConfirm(false)}>
                ✕
              </button>
            </form>
            <h3 className="font-bold text-lg mb-4">Confirm Retry Migration</h3>
            <p className="mb-6">
              Are you sure you want to retry this failed migration? This will
              create a new migration run with the same parameters.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setShowRetryConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={handleRetryMigration}
                disabled={isRetrying}>
                {isRetrying ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Retrying...
                  </>
                ) : (
                  'Retry Migration'
                )}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};
